import { Component, inject, signal } from '@angular/core';
import { NgIf } from '@angular/common';
import { AssetVersionPipe } from '../../../../pipes/asset-version.pipe';
import { ShortNumberPipe } from '../../../../pipes/short-number.pipe';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilService } from '../../../../services/util.service';
import { CreativeGraphPoint, SocialCreativeCardModalData } from '../../types';
import { SocialPlatformIconsComponent } from '../platform-icons/platform-icons.component';
import { PerformanceOverTimeComponent } from '../../../../shared/components/charts/performance-over-time/performance-over-time.component';
import { PerformanceChartExtraSettings } from '../../../../shared/components/charts/performance-over-time/performance-over-time.types';
import { MyCompetitionPerformanceWidgetResponse } from '../../../my-competition-screen/types';

@Component({
  selector: 'app-social-creative-card-modal',
  imports: [NgIf, ShortNumberPipe, AssetVersionPipe, SocialPlatformIconsComponent, PerformanceOverTimeComponent],
  templateUrl: './social-creative-card-modal.component.html',
  styleUrl: './social-creative-card-modal.component.scss'
})
export class SocialCreativeCardModalComponent {
  public data!: SocialCreativeCardModalData;
  public performanceData?: MyCompetitionPerformanceWidgetResponse;
  public brandColors: Record<string, string> = {};
  public chartAxisMin?: number;
  public chartAxisMax?: number;
  public chartExtraSettings: Partial<PerformanceChartExtraSettings> = {};
  public isPlaying = signal(false);
  public hasStarted = signal(false);
  private readonly noCreativeDataText = 'Creative failed to load. Please try again.';
  private readonly chartLineColor = '#0A5C5B';
  private dialogData = inject(MAT_DIALOG_DATA);
  private utilService = inject(UtilService);

  constructor(private dialogref: MatDialogRef<SocialCreativeCardModalComponent>) {
    if (!this.dialogData) {
      this.utilService.openSnackBar(this.noCreativeDataText, { variant: 'error' });
      this.dialogref.close();
    }
    this.setData();
    this.buildChartData();
  }

  public close() {
    this.dialogref.close();
  }

  public onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = new AssetVersionPipe().transform('images/no-logo.png');
  }

  public onCreativeError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = new AssetVersionPipe().transform('svg/empty-creative.svg');
    img.classList.add('creative-image--placeholder');
  }

  public get formattedAdType(): string {
    return this.utilService.formatAdType(this.data.adType);
  }

  public get isLinkedInVideo(): boolean {
    return this.data.isVideo && (this.data.platforms ?? []).every(p => p.toLowerCase() === 'linkedin');
  }

  public copyCtaLink(): void {
    if (!this.data.ctaLink) return;
    navigator.clipboard.writeText(this.data.ctaLink);
    this.utilService.openSnackBar('CTA link copied');
  }

  private setData() {
    this.data = {
      impressions: this.dialogData.impressions,
      cpm: this.dialogData.cpm,
      creativeUrl: this.dialogData.creativeUrl,
      videoUrl: this.dialogData.videoUrl,
      creativeId: this.dialogData.creativeId,
      description: this.dialogData.description || 'No creative text available',
      adType: this.dialogData.adType,
      platforms: this.dialogData.platforms,
      logoPath: this.dialogData.logoPath,
      brandName: this.dialogData.brandName,
      brandKey: this.dialogData.brandKey,
      firstSeen: this.dialogData.firstSeen ? new Date(this.dialogData.firstSeen).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A',
      lastSeen: this.dialogData.lastSeen ? new Date(this.dialogData.lastSeen).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A',
      ctaText: this.dialogData.ctaText || 'Learn more',
      ctaLink: this.dialogData.ctaLink,
      isVideo: this.dialogData.isVideo,
    }
  }

  private buildChartData(): void {
    const graphData: CreativeGraphPoint[] = this.dialogData.graphData ?? [];
    const dailyPerformance = graphData.map(({ date, impressions }) => ({ date, value: impressions }));
    if (!dailyPerformance.length) return;
    this.performanceData = {
      isUnderDateThreshold: false,
      myBrand: {
        brandKey: this.dialogData.brandKey,
        brandName: this.dialogData.brandName ?? this.dialogData.brandKey,
        dailyPerformance,
      },
    };
    this.brandColors = { [this.dialogData.brandKey]: this.chartLineColor };
    this.chartAxisMin = this.dateToMilliseconds(this.dialogData.rangeStart);
    this.chartAxisMax = this.dateToMilliseconds(this.dialogData.rangeEnd);
    this.chartExtraSettings = {
      chartHeight: 120,
      showLegend: false,
      xAxisType: 'datetime',
      axisMin: this.chartAxisMin,
      axisMax: this.chartAxisMax,
      labelRotation: 0,
      endpointTicksOnly: true,
    };
  }

  private dateToMilliseconds(date: Date | null): number | undefined {
    if (!date) return undefined;
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  }
}
